import React, { useCallback, useContext, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { SalesOrder } from '../../../graphql/entities';
import { alertAction, handleComponentQueryError } from '../../../utils/parseErrorResponse';
import getClientMutationId from '../../../utils/getClientMutationId';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import setIn from '../../../components/connectedForm/utils/setIn';
import DeprecatedConfirmationPrompt from '../../../components/modals/ConfirmationPrompt';
import debounce from 'es6-promise-debounce';
import { ISalesOrder, ISalesOrderInput, Maybe, PriceSelectionPermission } from '../../../graphql/schema/p4types';
import { IReduxStore } from '../../../@types/reduxTypes';
import FluentModal from '../../../components/modals/FluentModal';

//Custom Hook for Trade Counter.
//Returns a function for triggering a save to the sales order, which will update state with the given setter.
//Returns a confirmation prompt element that needs to be returned with the render from the component.
export const useCustomSalesOrderUpdate = () => {
  const [salesOrder, setSalesOrder] = useState<ISalesOrderInput>({});

  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const [confirmationState, setConfirmationState] =
    useState<Maybe<{ meta: any; title: string; message: string }>>(null);
  const [saveSalesOrder] = useMutation(SalesOrder.update!, {
    onError: (data) =>
      handleComponentQueryError(data, {
        dispatch,
        entity: SalesOrder,
        name: 'Unable to save Sales Order',
        customAlertHandler: (_: unknown, meta: any, title: string, message: string) => {
          if (meta.confirmationReference) {
            setConfirmationState({
              meta,
              title,
              message,
            });
            return;
          }
          //If no custom handling, call the default handling.
          alertAction({ dispatch, entity: connectedForm?.entity }, meta, title, message);
        },
      }),
  });

  const onCompleteRef = useRef<Maybe<(updatedSalesOrder: ISalesOrder) => void>>(null);

  const onSubmit = (newOrder: ISalesOrderInput, onComplete: Maybe<(updatedSalesOrder: ISalesOrder) => void>) => {
    onCompleteRef.current = onComplete;
    saveSalesOrder({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesOrder: normalizeVariables(newOrder),
        },
      },
    }).then((result) => {
      if (result?.data) {
        const updatedSalesOrder = result.data.salesOrderUpdate.result;

        setSalesOrder(updatedSalesOrder);
        onComplete?.(updatedSalesOrder as ISalesOrder);
      }
    });
  };

  const responseCount = confirmationState?.meta.responses?.length || 2;

  //Multi-Option Response Handling.
  const confirmPromptResponse = (value: any) => {
    const field = `confirmationResponses.${[confirmationState?.meta.confirmationReference]}`;

    setSalesOrder((currentSalesOrder) => {
      const newSalesOrder = setIn(currentSalesOrder, field, value);

      setConfirmationState(null);
      onSubmit(newSalesOrder, onCompleteRef?.current);
      return newSalesOrder;
    });
  };

  const confirmationPrompt = (
    <FluentModal isOpen={!!confirmationState}>
      <div style={{ width: `${350 + responseCount * 100}px`, minHeight: '250px', position: 'relative' }}>
        <DeprecatedConfirmationPrompt
          onReject={() => setConfirmationState(null)}
          title="Confirmation Required"
          confirm={() => confirmPromptResponse(true)}
          message={confirmationState?.message}
          confirmationResponses={confirmationState?.meta.responses}
          onConfirmPromptResponse={confirmPromptResponse}
        />
      </div>
    </FluentModal>
  );

  return { salesOrder, setSalesOrder, save: onSubmit, confirmationPrompt };
};

/**
 * Debounced State Hook
 * Returns an array: [
 * immediateValue - The most up to date value, e.g. for display in an input box.
 * debouncedValue - The delayed value, e.g. for triggering a new service call.
 * updateValue - Function to update the stored values.
 * updateImmediateValue - Update the stored values, skipping the debounce step (I.e. Used for initating the value from props changes).
 * ]
 */
export const useDebouncedState = <ValueType,>(
  initialState: ValueType,
  wait = 500,
  { onDebounce }: { onDebounce?: (value: ValueType) => void } = {}
): [ValueType, ValueType, React.Dispatch<React.SetStateAction<ValueType>>] => {
  const [immediateValue, setImmediateValue] = useState<ValueType>(initialState);
  const [debouncedValue, setDebouncedValue] = useState<ValueType>(initialState);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updatedDebounced = useCallback(
    debounce((value: ValueType) => {
      onDebounce?.(value);
      setDebouncedValue(value);
    }, wait),
    []
  );

  const updateValue = useCallback(
    (value: ValueType) => {
      setImmediateValue(value);
      updatedDebounced(value);
    },
    [updatedDebounced]
  );

  const updateImmediateValue = useCallback((value: ValueType) => {
    setImmediateValue(value);
    setDebouncedValue(value);
  }, []);

  return [immediateValue, debouncedValue, updateValue, updateImmediateValue];
};

export const usePriceSelectionPermission = () => {
  const app = useSelector((store: any) => store.app);

  return app?.fullUserDetails?.companyUser?.priceSelectionPermission || PriceSelectionPermission.None;
};

export const useManualPricingRequiresApproval = () => {
  return useSelector(
    (store: IReduxStore) => store.app?.fullUserDetails?.companyUser?.manualPricingRequiresApproval ?? false
  );
};

export const useCanApproveManualPrices = () => {
  return useSelector(
    (store: IReduxStore) =>
      store.app?.fullUserDetails?.systemUser?.admin ||
      store.app?.fullUserDetails?.companyRoleActions?.some(
        (companyRoleAction) =>
          companyRoleAction?.action?.name === 'SALES_ORDER_LINES_MANUAL_PRICE_APPROVAL' && companyRoleAction?.permitted
      )
  );
};
